import { useCallback, useEffect, useMemo, useState } from 'react';
import useLocations from '../../hooks/use-locations';
import SmallTextInput from '../forms/small-text-input.component';
import { LocationBackButton, LocationListHolder, LocationListItem, LocationListItemImage } from './posts.styled';
import { Body, SmallBody } from '../typography.styled';
import { FlexContainer } from '../components.styled';
import useCurrentUser from '../../hooks/use-current-user';
import { RegionDetail } from '../../types/location/region-detail';
import Pagination from '../basic/pagination.component';
import { IonIcon } from '@ionic/react';
import { home as homeFilled, arrowBackOutline, arrowForwardOutline, globeOutline } from 'ionicons/icons';

interface LocationOption {
    id: number;
    name: string;
    isHome: boolean;
    isRegion: boolean;
    isBack: boolean;
}
const toOptions = (region: RegionDetail): LocationOption[] => {
    const ret = [
        ...region.childRegions.map(
            (r) =>
                ({
                    id: r.regionId,
                    name: r.name,
                    isHome: false,
                    isRegion: true,
                } as LocationOption),
        ),
        ...region.locations.map(
            (l) =>
                ({
                    id: l.locationId,
                    name: l.name,
                    isHome: false,
                    isRegion: false,
                } as LocationOption),
        ),
    ];
    ret.sort((a, b) => {
        if (a.isHome) return -1;
        if (b.isHome) return 1;
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });
    return ret;
};

interface LocationSelectProps {
    selectedId: number | null;
    small?: boolean;
    hideGlobal?: boolean;
    showHomeAtTop?: boolean;
    setSelectedId: (id: number | null) => void;
}
const LocationSelect = ({ selectedId, small, hideGlobal, showHomeAtTop, setSelectedId }: LocationSelectProps) => {
    const { locations, regions, getLocation, getRegion } = useLocations();
    const [search, setSearch] = useState('');
    const [regionTree, setRegionTree] = useState<LocationOption[][]>([]);
    const [regionTreeCursor, setRegionTreeCursor] = useState<number>(0);

    const currentUser = useCurrentUser();
    const homeId = currentUser.user?.homeBaseId;
    const home = useMemo(() => getLocation(homeId ?? 0), [getLocation, homeId]);

    useEffect(() => {
        if (regions.length > 0 && regionTree.length === 0) {
            setRegionTree([toOptions(getRegion(null))]);
        }
    }, [getRegion, regionTree.length, regions]);

    const searchResults = useMemo(() => {
        return locations
            .filter((l) => search === '' || l.name.toLowerCase().indexOf(search.toLowerCase()) >= 0)
            .map(
                (l) =>
                    ({
                        id: l.locationId,
                        name: l.name,
                        isHome: false,
                        isRegion: false,
                    } as LocationOption),
            );
    }, [locations, search]);

    const select = useCallback(
        (option: LocationOption) => {
            if (!option.isRegion) {
                setSelectedId(option.id);
            } else if (option.isBack) {
                setRegionTreeCursor(regionTreeCursor - 1);
            } else {
                const nextLayer = toOptions(getRegion(option.id));
                setRegionTree((t) => [...t.slice(0, regionTreeCursor + 1), nextLayer]);
                setRegionTreeCursor(regionTreeCursor + 1);
            }
        },
        [getRegion, regionTreeCursor, setSelectedId],
    );
    const back = useCallback(() => {
        setRegionTreeCursor((c) => c - 1);
    }, []);

    return (
        <FlexContainer direction="column">
            <SmallTextInput id="location-search" label="Search Locations" value={search} setValue={setSearch} />
            {search ? (
                <LocationListHolder $small={small}>
                    {searchResults.map((o) => (
                        <LocationListItem
                            onClick={() => select(o)}
                            key={o.isRegion ? 0 - o.id : o.id}
                            $isSelected={!o.isRegion && selectedId === o.id}
                            $small={small}
                        >
                            <LocationListItemImage />
                            <p>
                                <Body>{o.name} </Body>
                            </p>
                        </LocationListItem>
                    ))}
                </LocationListHolder>
            ) : (
                <Pagination pageOn={regionTreeCursor}>
                    {regionTree.map((rt, i) => (
                        <div key={i}>
                            {i > 0 && (
                                <LocationBackButton onClick={back}>
                                    <IonIcon icon={arrowBackOutline} />
                                    <SmallBody>Back</SmallBody>
                                </LocationBackButton>
                            )}
                            <LocationListHolder $small={small}>
                                {i === 0 && showHomeAtTop && home && (
                                    <LocationListItem
                                        onClick={() =>
                                            select({
                                                id: home?.locationId ?? 0,
                                                name: '',
                                                isHome: true,
                                                isBack: false,
                                                isRegion: false,
                                            })
                                        }
                                        key={-1}
                                        $isSelected={selectedId === homeId}
                                        $small={small}
                                    >
                                        <LocationListItemImage />
                                        <p>
                                            <IonIcon icon={homeFilled} />
                                            <Body>{home.name}</Body>
                                        </p>
                                    </LocationListItem>
                                )}
                                {i === 0 && !hideGlobal && (
                                    <LocationListItem
                                        onClick={() =>
                                            select({
                                                id: 0,
                                                name: 'Global',
                                                isHome: false,
                                                isBack: false,
                                                isRegion: false,
                                            })
                                        }
                                        key={0}
                                        $isSelected={selectedId === 0}
                                        $small={small}
                                    >
                                        <LocationListItemImage />
                                        <p>
                                            <IonIcon icon={globeOutline} />
                                            <Body>Global</Body>
                                        </p>
                                    </LocationListItem>
                                )}
                                {rt.map((o) => (
                                    <LocationListItem
                                        onClick={() => select(o)}
                                        key={o.isRegion ? 0 - o.id : o.id}
                                        $isSelected={!o.isRegion && selectedId === o.id}
                                        $small={small}
                                    >
                                        <LocationListItemImage />
                                        <p>
                                            {!o.isRegion && o.id === homeId && <IonIcon icon={homeFilled} />}
                                            <Body>{o.name}</Body>
                                            {o.isRegion && <IonIcon icon={arrowForwardOutline} />}
                                        </p>
                                    </LocationListItem>
                                ))}
                            </LocationListHolder>
                        </div>
                    ))}
                </Pagination>
            )}
        </FlexContainer>
    );
};

export default LocationSelect;
